import { useEffect, useState } from "react";
import { Loader2, LogOut, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

const DELAY_MS = 1500;
const QUESTIONS = ["unsub.q1", "unsub.q2", "unsub.q3", "unsub.q4"];
const CONFIRMS = ["unsub.yes1", "unsub.yes2", "unsub.yes3", "unsub.yes4"];
// Dialog is pinned to the top with a tall body: confirm buttons alternate top / bottom (and start / end) between
// consecutive steps, so repeated taps at one spot can never chain the 4 steps.
const LAYOUT = [
  ["confirm:self-end", "spacer", "cancel:self-start"],
  ["cancel:self-end", "spacer", "confirm:self-start"],
  ["confirm:self-start", "spacer", "cancel:self-end"],
  ["input", "cancel:self-end", "spacer", "confirm:self-stretch"],
];

const useArmed = (step, open) => {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    setArmed(false);
    if (!open) return undefined;
    const id = setTimeout(() => setArmed(true), DELAY_MS);
    return () => clearTimeout(id);
  }, [step, open]);
  return armed;
};

const ArmBar = ({ armed, step }) => (
  <span className="block h-1 w-full overflow-hidden rounded-full bg-neutral-800" aria-hidden>
    <span key={step} className={`block h-full rounded-full bg-red-500 ${armed ? "w-full" : "w-0 animate-[unsubArm_1.5s_linear_forwards]"}`} />
  </span>
);

export const UnsubscribeFlow = ({ billing, onChange, url = "/dev/billing/unsubscribe", testId = "billing-unsubscribe-btn", children = null }) => {
  const { t, fmtDate } = useI18n();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const armed = useArmed(step, open);
  const target = t("unsub.word");
  const date = fmtDate(billing.period_end);
  const start = () => {
    setStep(0);
    setWord("");
    setOpen(true);
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(url, { confirm: word });
      onChange(data);
      setOpen(false);
      toast.success(t("unsub.done", { date: fmtDate(data.period_end || data.until) }));
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      toast.error(detail.startsWith("Confirmation incorrecte") ? t("unsub.wrong", { word: target }) : translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  const confirm = () => (step < 3 ? setStep(step + 1) : submit());
  const disabled = !armed || busy || (step === 3 && !word.trim());
  return (
    <>
      <button type="button" onClick={start} className="btn-red btn-sm mt-4 inline-flex items-center gap-2 border-2 border-red-400" data-testid={testId}>
        <LogOut className="h-4 w-4 shrink-0" /> {t("unsub.button")}
      </button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="card-carbon !bg-[#0d0b0c] !top-[6vh] !translate-y-0 max-h-[88vh] w-[calc(100vw-2rem)] max-w-md overflow-y-auto rounded-2xl border-red-500/60 data-[state=open]:!slide-in-from-top-4 [&>button:last-child]:hidden" data-testid={`unsub-step-${step + 1}`}>
          <DialogClose className="absolute end-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-full border border-neutral-600 text-neutral-200 hover:text-white" aria-label={t("unsub.cancel")} data-testid="unsub-close-x">
            <X className="h-5 w-5" />
          </DialogClose>
          <p className="pe-12 text-xs font-semibold uppercase tracking-wider text-red-300" data-testid="unsub-step-label">{t("unsub.title")} · {t("unsub.step", { n: step + 1 })}</p>
          <DialogTitle className="break-words text-lg font-semibold leading-snug text-white">{t(QUESTIONS[step], { date, word: target })}</DialogTitle>
          <DialogDescription className="sr-only">{t("unsub.title")}</DialogDescription>
          {children}
          <div className="flex min-h-[22rem] flex-col gap-3">
            {LAYOUT[step].map((slot) => {
              const [kind, align] = slot.split(":");
              if (kind === "spacer") return <div key="spacer" className="min-h-6 flex-1" aria-hidden />;
              if (kind === "input") {
                return (
                  <input key="input" value={word} onChange={(e) => setWord(e.target.value)} autoComplete="off" autoCapitalize="characters" spellCheck={false} placeholder={target}
                    className="w-full min-w-0 rounded-xl border border-red-500/60 bg-black/40 px-3 py-3 text-base text-white focus:border-red-400 focus:outline-none" data-testid="unsub-confirm-input" />
                );
              }
              if (kind === "cancel") {
                return (
                  <DialogClose key="cancel" className={`rounded-full border border-neutral-500 px-5 py-2.5 text-sm font-semibold text-neutral-100 hover:border-white ${align}`} disabled={busy} data-testid={`unsub-cancel-${step + 1}`}>
                    {t("unsub.cancel")}
                  </DialogClose>
                );
              }
              return (
                <button key="confirm" type="button" onClick={confirm} disabled={disabled} className={`btn-red inline-flex max-w-full flex-col items-stretch gap-1.5 break-words disabled:opacity-60 ${align}`} data-testid={`unsub-confirm-${step + 1}`}>
                  <span className="inline-flex min-w-0 items-center justify-center gap-2">{busy && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}<span className="min-w-0 break-words">{t(CONFIRMS[step])}</span></span>
                  <ArmBar armed={armed} step={step} />
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const UnsubscribedNotice = ({ billing, onChange, url = "/dev/billing/resubscribe", testId = "billing-unsubscribed-notice" }) => {
  const { t, fmtDate } = useI18n();
  const [busy, setBusy] = useState(false);
  const resume = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(url);
      onChange(data);
      toast.success(t("unsub.resumed", { date: fmtDate(data.period_end || data.until) }));
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 rounded-xl border border-amber-400/60 bg-amber-950/40 p-4" data-testid={testId}>
      <p className="break-words text-sm font-semibold text-amber-100">{t("unsub.done", { date: fmtDate(billing.period_end) })}</p>
      <button type="button" onClick={resume} disabled={busy} className="btn-gold btn-sm mt-3" data-testid={testId === "billing-unsubscribed-notice" ? "billing-resubscribe-btn" : `${testId}-resubscribe`}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("unsub.resume")}
      </button>
    </div>
  );
};
